import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Divider,
  Box,
  Stack,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import httpRequest from "../utils/httpRequest";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";

const getRandomColor = () => {
  const colors = [
    "#FF5733",
    "#0a7025",
    "#3357FF",
    "#FF33A1",
    "#FF8C00",
    "#8A2BE2",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const HSNSelector = () => {
  const [open, setOpen] = useState(false);
  const [hsnList, setHsnList] = useState([]);
  const [newHsn, setNewHsn] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editingHsn, setEditingHsn] = useState(null);

  const fetchHsnList = async () => {
    try {
      const data = await httpRequest("hsn", "GET");
      setHsnList(data || []);
      
      if (editingHsn) {
        const stillExists = data?.some(item => item.hsn === editingHsn.hsn);
        if (!stillExists) {
          setEditingHsn(null);
          setNewHsn("");
          setNewDesc("");
        }
      }
    } catch (error) {
      console.error("Error fetching HSN codes:", error);
    }
  };

  const handleAddHsn = async () => {
    if (!newHsn.trim() || !newDesc.trim()) return;
    try {
      if (editingHsn) {
        await httpRequest(`hsn/${editingHsn.hsn}`, "PUT", { 
          hsn: newHsn, 
          description: newDesc 
        });
        setEditingHsn(null);
      } else {
        await httpRequest("hsn", "POST", { hsn: newHsn, description: newDesc });
      }
      setNewHsn("");
      setNewDesc("");
      fetchHsnList();
    } catch (error) {
      console.error("Error saving HSN code:", error);
    }
  };

  const handleDeleteHsn = async (hsnCode) => {
    if (!hsnCode) return;
    try {
      await httpRequest(`hsn/${hsnCode}`, "DELETE");
      fetchHsnList();
    } catch (error) {
      console.error("Error deleting HSN code:", error);
    }
  };

  const startEditing = (item) => {
    setEditingHsn(item);
    setNewHsn(item.hsn);
    setNewDesc(item.description);
  };

  const cancelEditing = () => {
    setEditingHsn(null);
    setNewHsn("");
    setNewDesc("");
  };

  const handleOpen = () => {
    setOpen(true);
    fetchHsnList();
  };

  const handleClose = () => {
    setOpen(false);
    setNewHsn("");
    setNewDesc("");
    setEditingHsn(null);
  };

  return (
    <div>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={<SettingsApplicationsIcon />}
      >
        Manage HSN
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available HSN Codes
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {hsnList.length > 0 ? (
              hsnList.map((item) => (
                <Box
                  key={item.hsn}
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    "&:hover .action-buttons": {
                      opacity: 1,
                      visibility: "visible",
                    },
                  }}
                >
                  <Chip
                    label={`${item.hsn} - ${item.description}`}
                    sx={{
                      backgroundColor: getRandomColor(),
                      color: "white",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: getRandomColor(),
                      },
                    }}
                    variant="filled"
                    onClick={() => startEditing(item)}
                  />
                  <Box
                    className="action-buttons"
                    sx={{
                      position: "absolute",
                      right: 2,
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: 0,
                      visibility: "hidden",
                      transition: "opacity 0.2s, visibility 0.2s",
                      display: "flex",
                      gap: 0.5,
                      zIndex: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(item);
                      }}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        color: "primary.main",
                        width: 20,
                        height: 20,
                        "&:hover": {
                          backgroundColor: "white",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteHsn(item.hsn);
                      }}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        color: "error.main",
                        width: 20,
                        height: 20,
                        "&:hover": {
                          backgroundColor: "white",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                No HSN codes found.
              </Typography>
            )}
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" gutterBottom>
            {editingHsn ? "Edit HSN Code" : "Add New HSN"}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              placeholder="HSN Code"
              value={newHsn}
              onChange={(e) => setNewHsn(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              placeholder="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              size="small"
              sx={{ flex: 2 }}
            />
            <IconButton
              onClick={handleAddHsn}
              color="primary"
              sx={{ border: "1px solid", borderRadius: 1 }}
            >
              {editingHsn ? <EditIcon /> : <AddIcon />}
            </IconButton>
            {editingHsn && (
              <IconButton
                onClick={cancelEditing}
                color="inherit"
                sx={{ border: "1px solid", borderRadius: 1 }}
              >
                ✕
              </IconButton>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleClose} size="small" color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HSNSelector;
